import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSocket } from '../../../context/SocketContext';
import { NewConversationPayload } from '../../../types/Messaging/NewConversationPayload';
import { NewMessagePayload } from '../../../types/Messaging/NewMessagePayload';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { useDmMessages } from './useDmMessages';

export default function useDirectMessaging(conversation_id?: number) {
  const { socket, isConnected } = useSocket();
  const [joinedConversation, setJoinedConversation] = useState<boolean>(false);
  const [isOnlineStatus, setIsOnlineStatus] = useState<{
    recipient: { isOnline: boolean; lastSeen?: string | null };
    user: { isOnline: boolean; lastSeen?: string | null };
  }>({
    recipient: { isOnline: false },
    user: { isOnline: false },
  });
  const isTypingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isTyping, setIsTyping] = useState<{
    recipient: boolean;
    user: boolean;
  }>({
    recipient: false,
    user: false,
  });
  const userProfile = useSelector((state: RootState) => state.userProfile);
  const queryClient = useQueryClient();
  const {
    queueMessage,
    setMessageTimeout,
    getQueuedMessage,
    updateQueue,
    saveToMessage,
    updateReadMessage,
    queue,
    messages,
  } = useDmMessages(conversation_id);

  //===============================================================================================================
  // CREATE NEW CONVERSATION
  const createNewCon = (payload: NewConversationPayload) => {
    if (!socket || joinedConversation) return;
    socket.emit('event.create.conversation', payload);

    // LISTEN FOR Conversation Creation
    socket.on('conversation.created', (data) => {
      console.log('==== New Conversation has been created');
      conversation_id = data.conversation.id;
    });
  };

  // JOIN CONVERSATION
  const connectToConversation = () => {
    if (!socket || !isConnected || !conversation_id) return;
    socket.emit('event.join.conversation', { conversationId: conversation_id });
  };

  // LEAVE CONVERSATION
  const exitConversation = () => {
    if (!socket || !joinedConversation || !conversation_id) return;
    socket.emit('event.leave.conversation', {
      conversationId: conversation_id,
    });
  };

  // SEND MESSAGE TO RECIPIENT
  const sendTextMessage = (msg: string) => {
    if (!socket || !userProfile?.role.id) return;
    if (!msg.trim()) return;

    const queuedMsgId = crypto.randomUUID();

    socket.emit('event.send.message', {
      clientMessageId: queuedMsgId,
      conversation_id,
      content: msg,
      type: 'text',
    } as NewMessagePayload);

    queueMessage(queuedMsgId, msg);
    setMessageTimeout(queuedMsgId);
  };

  const resendingTextMessage = (queuedMsgId: string) => {
    if (!socket) return;
    const messageInQueue = getQueuedMessage(queuedMsgId);

    if (messageInQueue) {
      socket.emit('event.send.message', {
        clientMessageId: queuedMsgId,
        conversation_id,
        content: messageInQueue.body,
        type: 'text',
      } as NewMessagePayload);
      updateQueue(queuedMsgId, {
        isSent: false,
        isSending: true,
        isError: false,
      });
      setMessageTimeout(queuedMsgId);
    }
  };

  // Emit USER TYPING STATUS (debounced so status stays true while user keeps typing)
  const TYPING_TIMEOUT_MS = 1_000;
  const handleUserTyping = () => {
    if (!socket || !conversation_id) return;

    // emit 'typing: true' only when the user wasn't already marked as typing
    setIsTyping((prev) => {
      if (!prev.user) {
        socket.emit('event.typing', {
          conversationId: conversation_id,
          isTyping: true,
        });
      }
      return { ...prev, user: true };
    });

    // reset the debounce timer so recipient sees typing until the user stops
    if (isTypingTimeout.current) {
      clearTimeout(isTypingTimeout.current);
    }

    isTypingTimeout.current = setTimeout(() => {
      setIsTyping((prev) => ({ ...prev, user: false }));
      socket.emit('event.typing', {
        conversationId: conversation_id,
        isTyping: false,
      });
      isTypingTimeout.current = null;
    }, TYPING_TIMEOUT_MS);
  };

  const markAsRead = useCallback(
    (messageIds: number[]) => {
      if (messageIds.length < 1 || !socket) return;

      if (messageIds.length == 1) {
        return socket.emit('event.message.read', {
          messageId: messageIds[0],
          conversationId: conversation_id,
        });
      }
      return socket.emit('event.messages.read.bulk', {
        messageIds,
        conversationId: conversation_id,
      });
    },
    [socket]
  );

  /// HANDLE CREATION OF NEW CONVERSATION
  useEffect(() => {
    if (!socket) return;
    return () => {
      socket.off('conversation.created');
    };
  }, [conversation_id, joinedConversation, socket]);

  //================================ EVENT LISTENERS ==================================================================
  useEffect(() => {
    if (!socket || !conversation_id) return;

    // CONNECT ON MOUNT
    if (!joinedConversation) connectToConversation();

    //LISTEN IF USER HAS JOINED CONVERSATION
    socket.on('conversation.joined', () => {
      console.log(
        `==== SUCCESSFULLY CONNECTED TO CONVERSATION-${conversation_id}`
      );
      setIsOnlineStatus((prev) => ({ ...prev, user: { isOnline: true } }));
      setJoinedConversation(true);
    });

    //LISTEN IF USER HAS DISCONNECTED FROM CONVERSATION
    socket.on('conversation.left', () => setJoinedConversation(false));

    // LISTEN FOR ERRORS
    socket.on('conversation.error', (data) => {
      console.error(data.reason);
    });

    // LISTEN FOR RECIPIENT TYPING STATUS
    socket.on('user.typing', (data) => {
      if (data.roleId !== userProfile?.role.user_id) {
        setIsTyping((prev) => ({ ...prev, recipient: data.isTyping }));
      }
    });

    //MESSAGE SENT STATUS EVENT
    socket.on('message.sent', (data: any) => {
      updateQueue(data?.clientMessageId);
    });

    //MESSAGE DENIED STATUS EVENT
    socket.on('message.error', (error) => {
      console.error(error);
      updateQueue(error?.clientMessageId, {
        isSent: false,
        isSending: false,
        isError: true,
      });
    });

    //TRACK WHEN RECIPIENT  ENTERS CONVERSATION
    socket.on('participant.joined', () => {
      setIsOnlineStatus((prev) => ({ ...prev, recipient: { isOnline: true } }));
    });

    //TRACK WHEN RECIPIENT LEAVES CONVERSATION
    socket.on('participant.left', ({ timestamp: lastSeen }) => {
      setIsOnlineStatus((prev) => ({
        ...prev,
        recipient: { isOnline: false, lastSeen },
      }));
    });

    //GET INCOMING MESSAGES
    socket.on('conversation.message', (data) => {
      const newMsg = { ...data.message, reads: [] };
      saveToMessage(newMsg);
      // Bubble the affected conversation to the top of the list (WhatsApp-style)
      // — the backend already sorts by last message, we just need to refetch.
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    });

    // Listen for single message read receipt
    socket.on(
      'message.read',
      (data: {
        messageId: number;
        userId: number;
        roleId: number;
        readAt?: string;
      }) => {
        updateReadMessage(data);
      }
    );

    // Listen for bulk message read receipts
    socket.on(
      'messages.read.bulk',
      (data: {
        messageIds: number[];
        conversationId: number;
        userId: number;
        roleId: number;
        timestamp?: string;
      }) => {
        for (const messageId of data.messageIds) {
          updateReadMessage({
            messageId,
            roleId: data.roleId,
            userId: data.userId,
            readAt: data.timestamp,
          });
        }
      }
    );

    // Listen for join confirmation
    return () => {
      socket.off('conversation.joined');
      socket.off('conversation.left');
      socket.off('conversation.error');
      socket.off('conversation.message');
      socket.off('user.typing');
      socket.off('message.sent');
      socket.off('message.error');
      socket.off('message.read');
      socket.off('messages.read.bulk');
      if (isTypingTimeout.current) clearTimeout(isTypingTimeout?.current);
      if (joinedConversation) exitConversation();
    };
  }, [socket, conversation_id, joinedConversation, userProfile]);

  return {
    connectToCon: connectToConversation,
    disconnectFromCon: exitConversation,
    connectedToCon: joinedConversation,
    conversation_id,
    createNewCon,
    handleUserTyping,
    sendTextMessage,
    isTyping,
    newMessages: messages,
    queuedMsg: queue,
    resendingTextMessage,
    markAsRead,
    participantsOnlineStatus: isOnlineStatus,
  };
}
